import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { Error } from "../../../common/components/AppState/Error";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { InputIcon, Input } from "../../../common/components/Input";
import SegmentedControl from "../../../common/components/SegmentedControl/SegmentedControl";
import {
  confirmMfaEnrollment,
  getMfaEnrollment,
  sendMfaStepUpCode,
  startMfaEnrollment,
} from "../state/mfaSlice";
import type { IMfaEnrollConfirmResponse, MfaMethod } from "../state/mfaSlice";

export const MFA_ENROLL_START_COMPONENT_ID = "mfaEnrollStartComponent";
export const MFA_ENROLL_CONFIRM_COMPONENT_ID = "mfaEnrollConfirmComponent";
const MFA_ENROLL_STEP_UP_COMPONENT_ID = "mfaEnrollStepUpComponent";
const STEP_UP_COOLDOWN_SECONDS = 30;

type WizardStep = "chooseAndAuth" | "setup" | "confirm";

interface MfaEnrollDialogProps {
  isOpen: boolean;
  /** D16: the user already has a confirmed factor and is replacing it (new
   * phone, or switching TOTP <-> email) rather than enrolling for the first
   * time. The backend gates this on password + a current code/recovery code
   * in addition to the password every enroll/start call already requires.
   * Always false when `mfaToken` is set — a confirmed-credential holder
   * always gets a "verify" challenge at login, never "enroll" (mfaEnrollStart.js). */
  isReplacement: boolean;
  /** The factor the user currently holds, when replacing one. This changes what
   * we are allowed to ASK for: mfaEnrollStart only accepts `current_otp` when
   * the existing credential is TOTP, so an email-factor holder can confirm
   * their identity with a recovery code and nothing else — there is no way to
   * have an email code sent from this dialog. Offering them "current code or
   * recovery code" is an instruction they cannot follow. */
  currentMethod?: "totp" | "email" | null;
  /** Cancel/backdrop/Escape — wipes the in-progress secret from state. Not
   * offered to the user in the `variant="inline"` case (see LoginForm.tsx) —
   * a mandated first login has no "cancel", only the credentials-form escape
   * hatch the parent already owns. */
  onClose: () => void;
  /** Enrollment confirmed. Profile flow (JWT, `mfaToken` unset): parent hands
   * off to RecoveryCodesDialog and `result` is undefined. Login flow
   * (`mfaToken` set, Phase 7): mfaEnrollConfirm.js returns the FULL login
   * payload alongside the recovery codes (Appendix D) — `result` carries it
   * so the caller can complete the same storage-and-navigate path `signIn`/
   * `verifyMfa` use, once the user has acknowledged the codes. Either way
   * this does NOT clear `state.mfa.recoveryCodes` — the codes dialog still
   * needs it. */
  onEnrolled: (result?: IMfaEnrollConfirmResponse) => void;
  /** Login-initiated enrollment (Phase 7, D6 mandate): the mfa_token from
   * leg 1's `mfa_enrollment_required` response. Its presence is what
   * switches the "chooseAndAuth" step from password re-entry to "already
   * authenticated a few seconds ago in leg 1" — see startMfaEnrollment's
   * mfaToken doc in mfaSlice.ts. */
  mfaToken?: string;
  /** "modal" (default) reproduces Phase 6's Profile-page popup unchanged.
   * "inline" renders the same wizard content without the ModalStandard
   * chrome, for LoginForm.tsx's mandated-enrollment step — the wizard IS
   * the page at that point, not an overlay on top of one. */
  variant?: "modal" | "inline";
}

const CODE_PATTERN = /^\d{6}$/;

/**
 * Three-step enroll wizard (docs/plans/todo/creator-mfa-two-factor-auth.md,
 * Phase 6 step 4): choose method + authenticate, set up (QR/secret or "email
 * sent"), confirm with a 6-digit code. Reused as-is for D16's "change method
 * or device" flow via `isReplacement`, and for Phase 7's login-driven
 * mandated enrollment via `mfaToken` + `variant="inline"`.
 */
const MfaEnrollDialog = ({
  isOpen,
  isReplacement,
  currentMethod = null,
  onClose,
  onEnrolled,
  mfaToken,
  variant = "modal",
}: MfaEnrollDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const enrollment = useAppSelector(getMfaEnrollment);

  const [step, setStep] = useState<WizardStep>("chooseAndAuth");
  const [method, setMethod] = useState<MfaMethod>("totp");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [currentCodeOrRecovery, setCurrentCodeOrRecovery] = useState("");
  const [confirmCode, setConfirmCode] = useState("");

  // "an email factor is not a one-way door" addendum (B3): the email
  // step-up challenge, minted on demand by mfaStepUpSend.js, that lets an
  // email-credential holder prove their CURRENT factor with an emailed code
  // instead of a recovery code. Ephemeral, component-local — unlike
  // `enrollment` above this never needs to survive a re-render of anything
  // else, so it isn't in Redux.
  const [stepUpToken, setStepUpToken] = useState<string | null>(null);
  const [stepUpSent, setStepUpSent] = useState(false);
  const [stepUpSending, setStepUpSending] = useState(false);
  const [stepUpCooldown, setStepUpCooldown] = useState(0);

  // Fresh wizard state every time it's (re)opened — a stale password or code
  // must never survive a previous attempt or a previous open of the dialog.
  useEffect(() => {
    if (isOpen) {
      setStep("chooseAndAuth");
      setMethod("totp");
      setPassword("");
      setCurrentCodeOrRecovery("");
      setConfirmCode("");
      setStepUpToken(null);
      setStepUpSent(false);
      setStepUpSending(false);
      setStepUpCooldown(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (stepUpCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setStepUpCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [stepUpCooldown]);

  const handleSendStepUp = () => {
    if (stepUpCooldown > 0 || stepUpSending) {
      return;
    }
    setStepUpSending(true);
    setStepUpSent(false);
    void dispatch(sendMfaStepUpCode({ componentId: MFA_ENROLL_STEP_UP_COMPONENT_ID }))
      .unwrap()
      .then((res) => {
        setStepUpToken(res.step_up_token);
        setStepUpSent(true);
        setStepUpSending(false);
        setStepUpCooldown(STEP_UP_COOLDOWN_SECONDS);
      })
      .catch(() => {
        // [fix 2026-08-07] A FAILED send must not look like a successful one.
        // Previously this started the cooldown and left the "code sent" state
        // alone, so a 429 (the limit is per user, per 15 minutes) rendered as a
        // countdown with no error — the user then waited for an email that was
        // never sent, typed nothing, and got a flat rejection. Found in a
        // browser, not in review.
        //
        // No cooldown on failure: nothing was sent, so there is nothing to
        // throttle, and blocking the button would strand them further. The
        // error surfaces through the shared <Error componentId={...}> slot.
        setStepUpSending(false);
        setStepUpSent(false);
      });
  };

  const handleStartEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmedCurrent = currentCodeOrRecovery.trim();
    const isSixDigitCode = CODE_PATTERN.test(trimmedCurrent);
    // See the currentMethod prop: a bare 6-digit entry only proves an
    // existing TOTP credential (verified directly against its secret). An
    // email credential has no such secret to check locally — a 6-digit entry
    // only counts once it's paired with a step-up token (B3, "an email
    // factor is not a one-way door"); otherwise a recovery code is the only
    // accepted proof, same as before that addendum existed.
    const canUseStepUp = currentMethod === "email" && Boolean(stepUpToken) && isSixDigitCode;
    const recoveryCodeOnly = currentMethod === "email" && !canUseStepUp;

    void dispatch(
      startMfaEnrollment({
        componentId: MFA_ENROLL_START_COMPONENT_ID,
        method,
        // Login flow: the mfa_token IS the re-authentication (password was
        // already checked in leg 1). Profile flow: no token, password required.
        ...(mfaToken ? { mfaToken } : { password }),
        ...(isReplacement && trimmedCurrent
          ? recoveryCodeOnly || !isSixDigitCode
            ? { recoveryCode: trimmedCurrent }
            : canUseStepUp
            ? { currentOtp: trimmedCurrent, stepUpToken: stepUpToken as string }
            : { currentOtp: trimmedCurrent }
          : {}),
      }),
    ).then((res) => {
      if (startMfaEnrollment.fulfilled.match(res)) {
        setStep("setup");
      }
    });
  };

  const handleConfirmEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // enroll/confirm resolves its actor by trying mfa_token before JWT
    // (resolveMfaActor.js) — the token from enroll/start must come back here
    // even on this JWT-authenticated Profile path, or the pending challenge
    // can't be found and confirm fails unconditionally.
    if (!enrollment?.mfaToken) {
      return;
    }

    void dispatch(
      confirmMfaEnrollment({
        componentId: MFA_ENROLL_CONFIRM_COMPONENT_ID,
        mfaToken: enrollment.mfaToken,
        otp: confirmCode.trim(),
      }),
    ).then((res) => {
      if (confirmMfaEnrollment.fulfilled.match(res)) {
        // Profile flow: payload is {ok, recovery_codes} - the caller ignores
        // it. Login flow: payload also carries the full login result - the
        // caller (LoginForm.tsx) needs it to complete storage-and-navigate
        // once the user has acknowledged the recovery codes.
        onEnrolled(res.payload);
      } else {
        // Rejected: clear the field. The input is already at maxLength, so a
        // browser silently DROPS a paste of the next code and the user is left
        // staring at the stale one, having to clear it by hand. Same fix as
        // MfaChallengeForm - see the comment there.
        setConfirmCode("");
      }
    });
  };

  // B1: the mandated org-admin enrolment screen (LoginForm.tsx renders this
  // dialog with variant="inline" for mfa_enrollment_required) used to be a
  // dead end - "inline" skips ModalStandard entirely (no close button, no
  // backdrop, no Escape), and every step rendered only a forward button. An
  // admin without their phone had nothing to click. Same markup/class as
  // MfaChallengeForm.tsx's "Back to sign in" link, on every step, wired to
  // `onClose` (LoginForm.tsx's handleBackToCredentials - returns to the
  // credentials form and clears the challenge + any in-progress secret).
  // Never rendered in the "modal" variant - ModalStandard's own close button
  // already covers that case, and "sign in" doesn't apply to a Profile-
  // initiated activate/replace flow.
  const backToSignIn =
    variant === "inline" ? (
      <button
        type="button"
        className="text-purple-500 font-medium text-xs border-0"
        onClick={onClose}
      >
        {t("pages.login.mfaChallenge.backToLogin")}
      </button>
    ) : null;

  const content = (
    <div className="flex w-full">
      <div className="completely-centered z-50">
        <IsLoading componentId={MFA_ENROLL_START_COMPONENT_ID} showSuccess={false} />
        <IsLoading componentId={MFA_ENROLL_CONFIRM_COMPONENT_ID} showSuccess={false} />
        <IsLoading componentId={MFA_ENROLL_STEP_UP_COMPONENT_ID} showSuccess={false} />
      </div>
      <div className="relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto">
        {step === "chooseAndAuth" && (
          <form onSubmit={handleStartEnrollment}>
            <h2 className="pt-3 pb-1 font-medium text-lg">
              {isReplacement
                ? t("pages.profile.mfa.enroll.replaceTitle")
                : t("pages.profile.mfa.enroll.title")}
            </h2>
            <p className="pb-3">{t("pages.profile.mfa.enroll.chooseMethodSubtitle")}</p>

            <SegmentedControl<MfaMethod>
              options={[
                { value: "totp", label: t("pages.profile.mfa.enroll.methodTotp") },
                { value: "email", label: t("pages.profile.mfa.enroll.methodEmail") },
              ]}
              value={method}
              onChange={setMethod}
              fullWidth
            />
            <p className="text-sm text-gray-500 mt-2">
              {method === "totp"
                ? t("pages.profile.mfa.enroll.methodTotpHint")
                : t("pages.profile.mfa.enroll.methodEmailHint")}
            </p>

            {!mfaToken && (
              <div className="mt-4">
                <InputIcon
                  autoFocus
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(event.target.value)
                  }
                  type={passwordVisible ? "text" : "password"}
                  position="right"
                  placeholder={t("pages.profile.main.changePassword.oldPasswordPlaceholder")}
                  handleClick={() => setPasswordVisible(!passwordVisible)}
                  icon={passwordVisible ? "EyeIcon" : "EyeOffIcon"}
                />
              </div>
            )}

            {isReplacement && (
              <div className="mt-2">
                <Input
                  type="text"
                  readOnly={false}
                  value={currentCodeOrRecovery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCurrentCodeOrRecovery(e.target.value)
                  }
                  className="font-medium bg-white text-gray-600 border-0"
                  placeholder={
                    // B3: once a step-up code has been emailed, a 6-digit
                    // entry is accepted again alongside a recovery code -
                    // same combined field as a TOTP holder gets, not a
                    // separate one (see handleStartEnrollment's canUseStepUp).
                    currentMethod === "email" && !stepUpToken
                      ? t("pages.profile.mfa.enroll.recoveryCodeOnlyPlaceholder")
                      : t("pages.profile.mfa.enroll.currentCodePlaceholder")
                  }
                />
                {currentMethod === "email" && (
                  <div className="flex justify-between items-center mt-1">
                    <button
                      type="button"
                      className="text-purple-500 font-medium text-xs border-0 disabled:text-gray-400"
                      onClick={handleSendStepUp}
                      disabled={stepUpCooldown > 0 || stepUpSending}
                    >
                      {stepUpCooldown > 0
                        ? t("pages.login.mfaChallenge.resendCooldown", { seconds: stepUpCooldown })
                        : t("pages.profile.mfa.enroll.emailStepUpButton")}
                    </button>
                  </div>
                )}
                <p
                  className={`text-xs text-green-600 mt-1 ${
                    stepUpSent && currentMethod === "email" ? "" : "invisible"
                  }`}
                >
                  {t("pages.profile.mfa.enroll.emailStepUpSent")}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {currentMethod === "email"
                    ? t("pages.profile.mfa.enroll.replaceHintEmail")
                    : t("pages.profile.mfa.enroll.replaceHint")}
                </p>
                <Error
                  componentId={MFA_ENROLL_STEP_UP_COMPONENT_ID}
                  errorString={t("pages.profile.mfa.enroll.stepUpSendFailed")}
                />
              </div>
            )}

            <Error componentId={MFA_ENROLL_START_COMPONENT_ID} />

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              <button
                className="btn-primary-fill ml-auto"
                type="submit"
                disabled={(!mfaToken && !password) || (isReplacement && !currentCodeOrRecovery)}
              >
                {t("general.next")}
              </button>
            </div>
          </form>
        )}

        {step === "setup" && (
          <div>
            <h2 className="pt-3 pb-1 font-medium text-lg">
              {t("pages.profile.mfa.enroll.setupTitle")}
            </h2>

            {enrollment?.method === "totp" ? (
              <>
                <p className="pb-3">{t("pages.profile.mfa.enroll.setupTotpInstructions")}</p>
                {enrollment.qrDataUri && (
                  <img
                    src={enrollment.qrDataUri}
                    alt={t("pages.profile.mfa.enroll.qrAlt")}
                    className="w-48 h-48 mx-auto"
                  />
                )}
                {enrollment.secret && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-500">
                      {t("pages.profile.mfa.enroll.manualEntryHint")}
                    </p>
                    <p className="select-all font-mono bg-white rounded-lg border border-gray-300 p-2 mt-1 break-all">
                      {enrollment.secret}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="pb-3">{t("pages.profile.mfa.enroll.setupEmailInstructions")}</p>
            )}

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              <button
                className="btn-primary-fill ml-auto"
                type="button"
                onClick={() => setStep("confirm")}
              >
                {t("general.next")}
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <form onSubmit={handleConfirmEnrollment}>
            <h2 className="pt-3 pb-1 font-medium text-lg">
              {t("pages.profile.mfa.enroll.confirmTitle")}
            </h2>
            <p className="pb-3">{t("pages.profile.mfa.enroll.confirmSubtitle")}</p>

            <Input
              autoFocus
              type="text"
              // See MfaChallengeForm: an OTP field should offer the
              // authenticator suggestion, not a history of past values.
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              readOnly={false}
              value={confirmCode}
              // D6: strip non-digits before maxLength applies - see
              // MfaChallengeForm.tsx's handleCodeChange/handleCodePaste for
              // why onPaste must intercept the clipboard directly rather than
              // clean up after the browser has already truncated the pasted
              // string to fit maxLength.
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                setConfirmCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
              }}
              className="font-medium bg-white text-gray-600 border-0 tracking-[0.3em] text-center"
              placeholder={t("pages.profile.mfa.enroll.confirmPlaceholder")}
              data-testid="mfa-enroll-code"
            />

            <Error componentId={MFA_ENROLL_CONFIRM_COMPONENT_ID} />

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              <button
                className="btn-primary-fill ml-auto"
                type="submit"
                disabled={!CODE_PATTERN.test(confirmCode.trim())}
              >
                {t("general.confirm")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  if (variant === "inline") {
    // No ModalStandard chrome (no backdrop, no close button) — this wizard
    // IS the login page's content at this step, not an overlay on top of one.
    return isOpen ? content : null;
  }

  return (
    <ModalStandard
      id="modalMfaEnroll"
      fullScreen={false}
      controlledMode={{ isOpen }}
      showCloseButton
      onClose={onClose}
    >
      {content}
    </ModalStandard>
  );
};

export default MfaEnrollDialog;
